/**
 * Approvals attached to a service request.
 *
 * Read-only in v1 by contract: approving and denying arrive later as governed
 * actions, not as ticket writes. Showing them anyway matters — "waiting for
 * approval" with no named approver is the most common reason an employee chases
 * IT about a request that is not actually stuck.
 */

import { Typography } from "@norican/react";

import { RelativeTime } from "@/components/common";
import { useT } from "@/i18n";

import type { TicketApproval } from "../api/contract";
import { ApprovalStatusBadge } from "./ticket-badges";
import { TranslatableTextView } from "./translatable-text";

export interface TicketApprovalsProps {
  approvals: TicketApproval[];
}

export function TicketApprovals({ approvals }: TicketApprovalsProps) {
  const { t } = useT("tickets");

  return (
    <ul className="flex flex-col gap-3">
      {approvals.map((approval) => (
        <li
          key={approval.approval_id}
          className="border-border-subtle flex flex-wrap items-center justify-between gap-3 border-b pb-3 last:border-0 last:pb-0"
        >
          <div className="flex min-w-0 flex-col gap-1">
            <TranslatableTextView value={approval.name} />
            <Typography variant="caption" tone="muted">
              {approval.approver == null
                ? t("approvals.noApprover")
                : t("approvals.approver", { name: approval.approver.display_name })}
              {approval.decided_at != null ? (
                <>
                  {" · "}
                  {t("approvals.decided")} <RelativeTime value={approval.decided_at} />
                </>
              ) : approval.due_at != null ? (
                <>
                  {" · "}
                  {t("approvals.due")} <RelativeTime value={approval.due_at} />
                </>
              ) : null}
            </Typography>
          </div>
          <ApprovalStatusBadge approval={approval} />
        </li>
      ))}
    </ul>
  );
}
